import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Modal from '../../components/Modal';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import { useCategoryTree } from '../../components/MaterialFieldInputs';
import { toast } from '../../utils/toast';
import {
    deleteTip,
    dupTitleSet,
    EMPTY_TIP_FILTER,
    filterTips,
    listAllTips,
    scopeText,
    Tip,
    TIP_TYPES,
    tipFilterCount,
    TipFilterState,
    TipOwnerKind,
    totalRefCount,
} from '../../api/tips';
import { Device, listDevices } from '../../api/device';
import {
    BulkMountModal,
    TipForm,
    TipIcon,
    TipRefsModal,
    TipTypeTag,
} from './TipWidgets';
import TipImportModal from './TipImportModal';
import BatchActions, {
    HeaderCheckbox,
    RowCheckbox,
} from '../../components/BatchActions';
import { useBatchSelect } from '../../utils/useBatchSelect';
import { confirmDialog, alertDialog } from '../../utils/dialog';
import { useCan } from '../../store';
import SearchSelect from '../../components/SearchSelect';
import { zhOptions } from '../../utils/searchOptions';
import { CRAFT_OPTS, LIGHT_OPTS } from './selectOptions';

// 小贴士库: 条目内容的唯一真源. 列表、筛选、编辑 (原地改, 所有引用同步生效)、
// 批量挂载、软删除. 全量取回后在前端筛选 —— 「显示 X / Y」「类型（N）」等
// 需要未筛选全集, 且与添加弹窗共用同一段过滤逻辑 (filterTips).

function errMsg(e: unknown): string {
    return e instanceof ApiError ? e.message : String(e);
}

type LibModal =
    | { kind: 'form'; tip?: Tip }
    | { kind: 'bulk'; tipCode: string; title: string; justCreated: boolean }
    | { kind: 'refs'; tip: Tip; dim: TipOwnerKind };

function TipLibrary() {
    const canCreate = useCan('tip', 'create');
    const canUpdate = useCan('tip', 'update');
    const canDelete = useCan('tip', 'delete');
    const canManage = canUpdate || canDelete;
    const canSeeDevice = useCan('device', 'get');
    const canSeeMaterial = useCan('material', 'get');
    const history = useHistory();
    const location = useLocation();
    const tree = useCategoryTree();
    const [all, setAll] = useState<Tip[] | null>(null);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState<TipFilterState>(EMPTY_TIP_FILTER);
    const [q, setQ] = useState('');
    const [modal, setModal] = useState<LibModal | null>(null);
    const [importOpen, setImportOpen] = useState(false);
    // 设备筛选 (派生维度, 2026-08-27): 设备 ~70 台, 一次拉全做下拉
    const [devices, setDevices] = useState<Device[]>([]);
    // ?edit={tipCode} (材料详情「到小贴士库编辑」链接) 只处理一次
    const editHandled = useRef(false);

    const reload = useCallback(async () => {
        try {
            const list = await listAllTips();
            setAll(list);
            setError('');
            return list;
        } catch (e) {
            setError(errMsg(e));
            setAll([]);
            return [];
        }
    }, []);

    useEffect(() => {
        void reload();
    }, [reload]);

    // 设备筛选的数据源: 没有设备查看权就不拉 (2026-09-13, 筛选本身也已隐藏)
    useEffect(() => {
        if (!canSeeDevice) return;
        listDevices({ pageSize: 500 })
            .then((res) => setDevices(res.list || []))
            .catch(() => setDevices([]));
    }, [canSeeDevice]);

    // 从材料详情跳转过来的库内编辑入口: 以无材料上下文打开同一条目
    useEffect(() => {
        if (editHandled.current || all === null) return;
        const code = new URLSearchParams(location.search).get('edit');
        if (!code) return;
        editHandled.current = true;
        const t = all.find((x) => x.tipCode === code);
        if (t && canUpdate) {
            setModal({ kind: 'form', tip: t });
            toast('已切到小贴士库 · 此处修改将对所有引用生效');
        } else if (t) {
            // 无编辑权: 不弹编辑表单, 只用搜索框定位到这条 (只读查看)
            setQ(t.title);
            toast('已定位到该条目 (只读)');
        }
        history.replace('/tips');
    }, [all, location.search, history, canUpdate]);

    const live = useMemo(() => all || [], [all]);
    // 类型筛选候选: 名称后带该类型条目数
    const typeOpts = useMemo(
        () =>
            TIP_TYPES.map((t) => ({
                value: t.value,
                label: `${t.label}（${live.filter((x) => x.type === t.value).length}）`,
            })),
        [live],
    );
    const dupTitles = useMemo(() => dupTitleSet(live), [live]);
    const cat1Node = tree.find((t) => t.categoryCode === filter.cat1);
    const cat1Names = cat1Node ? [cat1Node.nameZh, cat1Node.categoryCode] : [];
    // usedDevices 给的是设备名 (缺名时回退 device_key), 故两个都传, 与后端聚合口径对齐
    const devNode = devices.find((d) => d.deviceKey === filter.deviceKey);
    const devNames = devNode
        ? [devNode.name || devNode.deviceKey, devNode.deviceKey]
        : [];
    const shown = useMemo(
        () => filterTips(live, filter, cat1Names, q, devNames),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [live, filter, q, tree, devices],
    );
    const nf = tipFilterCount(filter);

    // 批量操作 (2026-09-10): 小贴士库不分页, 选中随筛选/搜索变化清空
    const sel = useBatchSelect(
        useMemo(() => shown.map((t) => t.tipCode), [shown]),
        [q, nf, JSON.stringify(filter)].join('|'),
    );

    const onDelete = async (t: Tip) => {
        const extra =
            totalRefCount(t) > 0
                ? `\n该条目当前挂载在 ${t.refCount} 个材料 / ${t.deviceRefCount} 台设备上，将一并解除。`
                : '';
        if (!(await confirmDialog(`确认删除「${t.title}」？${extra}`))) return;
        try {
            const { message } = await deleteTip(t.tipCode);
            // 走审批时条目未删, 不提示"已删除"; reload 拿到的仍是旧数据
            if (!alertApprovalIfPending(message)) toast('已删除');
            void reload();
        } catch (e) {
            alertDialog(`删除失败: ${errMsg(e)}`);
        }
    };

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-base font-semibold">小贴士库</div>
                    <div className="text-xs text-slate-500">
                        条目内容<b>单源</b>存于此表，材料详情页只做挂载引用 · 共{' '}
                        {live.length} 条
                    </div>
                </div>
                <div className="flex gap-2">
                    <input
                        className="inp w-64"
                        placeholder="搜索 条目ID / 标题 / 说明"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                    <button
                        type="button"
                        className="btn-ghost whitespace-nowrap"
                        onClick={() => setImportOpen(true)}
                        hidden={!canUpdate}
                    >
                        批量导入
                    </button>
                    <BatchActions
                        resource="tip"
                        entityLabel="小贴士"
                        deleteKeyField="tipCode"
                        selected={sel.selectedKeys}
                        onClear={sel.clear}
                        onDone={() => {
                            void reload();
                        }}
                    />
                    <button
                        type="button"
                        className="btn-primary whitespace-nowrap"
                        onClick={() => setModal({ kind: 'form' })}
                        hidden={!canCreate}
                    >
                        ＋ 新建条目
                    </button>
                </div>
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
                <SearchSelect
                    className="w-32"
                    value={filter.type}
                    onChange={(v) => setFilter({ ...filter, type: v })}
                    placeholder="全部类型"
                    options={typeOpts}
                    clearable
                />
                <SearchSelect
                    className="w-28"
                    value={filter.light}
                    onChange={(v) => setFilter({ ...filter, light: v })}
                    placeholder="全部光源"
                    options={LIGHT_OPTS}
                    clearable
                />
                <SearchSelect
                    className="w-28"
                    value={filter.craft}
                    onChange={(v) => setFilter({ ...filter, craft: v })}
                    placeholder="全部工艺"
                    options={CRAFT_OPTS}
                    clearable
                />
                <div hidden={!canSeeMaterial}>
                    <SearchSelect
                        className="w-36"
                        value={filter.cat1}
                        onChange={(v) => setFilter({ ...filter, cat1: v })}
                        placeholder="全部一级分类"
                        options={zhOptions(
                            tree,
                            (t) => t.categoryCode,
                            (t) => t.nameZh,
                        )}
                        clearable
                    />
                </div>
                <div hidden={!canSeeDevice}>
                    <SearchSelect
                        className="w-40"
                        value={filter.deviceKey}
                        onChange={(v) => setFilter({ ...filter, deviceKey: v })}
                        placeholder="全部设备"
                        options={devices.map((d) => ({
                            value: d.deviceKey,
                            label: d.name || d.deviceKey,
                        }))}
                        clearable
                    />
                </div>
                {nf > 0 && (
                    <button
                        type="button"
                        className="chip"
                        onClick={() => setFilter(EMPTY_TIP_FILTER)}
                    >
                        清空筛选 {nf}
                    </button>
                )}
                <span className="text-xs text-slate-400 ml-auto">
                    显示 {shown.length} / {live.length}
                    {(filter.light || filter.craft) && (
                        <span className="text-slate-300">
                            {' '}
                            · 含「全部光源／全部工艺」条目
                        </span>
                    )}
                </span>
            </div>

            {/* 表格收在一屏内: 短列 nowrap 固定住 (尤其「操作」列, 撑宽横滚就看不见了),
                富余宽度全给「标题 / 补充说明」列 (w-full 吸收剩余), 长文本在那列折行 */}
            <div className="card overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="th w-8" hidden={!canDelete}>
                                <HeaderCheckbox hidden={!canDelete} sel={sel} />
                            </th>
                            <th className="th w-14 whitespace-nowrap">图标</th>
                            <th className="th whitespace-nowrap">类型</th>
                            <th className="th w-full">标题 / 补充说明</th>
                            <th className="th whitespace-nowrap">适用范围</th>
                            <th className="th" hidden={!canSeeMaterial}>
                                耗材一级分类
                            </th>
                            <th className="th whitespace-nowrap">引用数</th>
                            <th
                                className="th whitespace-nowrap"
                                hidden={!canManage}
                            >
                                操作
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {all === null && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={8}
                                >
                                    加载中…
                                </td>
                            </tr>
                        )}
                        {all !== null && shown.length === 0 && (
                            <tr>
                                <td className="td text-slate-400" colSpan={8}>
                                    无匹配条目
                                </td>
                            </tr>
                        )}
                        {shown.map((t) => (
                            <tr key={t.tipCode} className="hover:bg-slate-50">
                                <td className="td" hidden={!canDelete}>
                                    <RowCheckbox
                                        hidden={!canDelete}
                                        sel={sel}
                                        k={t.tipCode}
                                    />
                                </td>
                                <td className="td">
                                    {t.icon ? (
                                        <TipIcon icon={t.icon} size="md" />
                                    ) : (
                                        <span className="text-slate-300">
                                            —
                                        </span>
                                    )}
                                </td>
                                <td className="td">
                                    <TipTypeTag type={t.type} />
                                </td>
                                <td className="td">
                                    <div className="text-[13px] font-medium">
                                        {t.title}
                                        {dupTitles.has(t.title) && (
                                            <span className="tag bg-amber-50 text-amber-700 ml-1.5">
                                                同名
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                                        {t.description}
                                    </div>
                                </td>
                                {/* 「全部光源 · 全部工艺」是一个整体, 不能断在中间 */}
                                <td className="td text-xs text-slate-500 whitespace-nowrap">
                                    {scopeText(t)}
                                </td>
                                <td
                                    className="td text-xs"
                                    style={{ maxWidth: 140 }}
                                    hidden={!canSeeMaterial}
                                >
                                    {t.usedCats.length > 0 ? (
                                        <a
                                            className="text-brand-600 cursor-pointer hover:underline"
                                            title="查看挂载了这条的材料"
                                            onClick={() =>
                                                setModal({
                                                    kind: 'refs',
                                                    tip: t,
                                                    dim: 'material',
                                                })
                                            }
                                        >
                                            {t.usedCats.join('、')}
                                        </a>
                                    ) : (
                                        '—'
                                    )}
                                </td>
                                <td className="td text-xs whitespace-nowrap">
                                    {/* 两个维度分别给, 各自可下钻; 不合成"总引用数".
                                        2026-09-13: 只显示自己看得到的那一侧 —— 设备管理员不该看到材料引用数, 反之亦然 */}
                                    <span hidden={!canSeeMaterial}>
                                        {t.refCount > 0 ? (
                                            <a
                                                className="text-brand-600 cursor-pointer hover:underline"
                                                onClick={() =>
                                                    setModal({
                                                        kind: 'refs',
                                                        tip: t,
                                                        dim: 'material',
                                                    })
                                                }
                                            >
                                                {t.refCount} 个材料
                                            </a>
                                        ) : (
                                            <span className="text-slate-300">
                                                0 个材料
                                            </span>
                                        )}
                                    </span>
                                    <span
                                        className="text-slate-300 mx-1"
                                        hidden={
                                            !canSeeMaterial || !canSeeDevice
                                        }
                                    >
                                        /
                                    </span>
                                    <span hidden={!canSeeDevice}>
                                        {t.deviceRefCount > 0 ? (
                                            <a
                                                className="text-brand-600 cursor-pointer hover:underline"
                                                onClick={() =>
                                                    setModal({
                                                        kind: 'refs',
                                                        tip: t,
                                                        dim: 'device',
                                                    })
                                                }
                                            >
                                                {t.deviceRefCount} 台设备
                                            </a>
                                        ) : (
                                            <span className="text-slate-300">
                                                0 台设备
                                            </span>
                                        )}
                                    </span>
                                </td>
                                <td
                                    className="td whitespace-nowrap text-xs"
                                    hidden={!canManage}
                                >
                                    <a
                                        className="text-brand-600 cursor-pointer hover:underline"
                                        onClick={() =>
                                            setModal({ kind: 'form', tip: t })
                                        }
                                        hidden={!canUpdate}
                                    >
                                        编辑
                                    </a>
                                    <a
                                        className="text-brand-600 cursor-pointer hover:underline ml-2"
                                        onClick={() =>
                                            setModal({
                                                kind: 'bulk',
                                                tipCode: t.tipCode,
                                                title: t.title,
                                                justCreated: false,
                                            })
                                        }
                                        hidden={!canUpdate}
                                    >
                                        批量挂载
                                    </a>
                                    <a
                                        className="text-rose-500 cursor-pointer hover:underline ml-2"
                                        onClick={() => onDelete(t)}
                                        hidden={!canDelete}
                                    >
                                        删除
                                    </a>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="text-[11px] text-slate-400">
                「适用范围」各维度<b>空 = 全部适用</b>。「耗材一级分类」是
                <b>派生列</b>
                ——由已挂载材料的一级分类聚合而来，条目本身不声明分类。材料与设备是
                <b>两个独立维度</b>，引用数分开给：点「N 个材料」「N
                台设备」看具体挂在哪些对象上，
                各自的展示顺序在对应详情页拖拽维护。
            </div>

            {/* 条目表单: 无材料上下文 → 库内编辑语义 (原地改, 所有引用同步生效) */}
            {modal?.kind === 'form' && (
                <Modal
                    open
                    width={720}
                    title={
                        modal.tip ? (
                            <>
                                编辑条目{' '}
                                <span className="text-xs text-slate-400 font-mono">
                                    {modal.tip.tipCode}
                                </span>
                            </>
                        ) : (
                            '新建条目'
                        )
                    }
                    onClose={() => setModal(null)}
                >
                    <TipForm
                        tip={modal.tip}
                        owner={null}
                        onCancel={() => setModal(null)}
                        onSaved={(openBulkFor) => {
                            if (openBulkFor) {
                                // 新建 general 条目 → 自动进入批量挂载 (带「条目已创建 · 」前缀)
                                setModal({
                                    kind: 'bulk',
                                    tipCode: openBulkFor.tipCode,
                                    title: openBulkFor.title,
                                    justCreated: true,
                                });
                            } else {
                                setModal(null);
                            }
                            void reload();
                        }}
                    />
                </Modal>
            )}
            {modal?.kind === 'bulk' && (
                <BulkMountModal
                    tipCode={modal.tipCode}
                    tipTitle={modal.title}
                    justCreated={modal.justCreated}
                    onClose={() => {
                        setModal(null);
                        void reload();
                    }}
                />
            )}
            <TipImportModal
                open={importOpen}
                onClose={() => setImportOpen(false)}
                onImported={() => void reload()}
            />
            {modal?.kind === 'refs' && (
                <TipRefsModal
                    tip={modal.tip}
                    initialDim={modal.dim}
                    onClose={() => setModal(null)}
                    onOpenMaterial={(xcode) => {
                        setModal(null);
                        if (canSeeMaterial)
                            history.push(
                                `/materials/${encodeURIComponent(xcode)}`,
                            );
                    }}
                    onOpenDevice={(deviceKey) => {
                        // 跳「设备树/工况」并选中该设备 —— 走 DeviceTopSelector 的 ?deviceKey 深链约定
                        setModal(null);
                        if (canSeeDevice)
                            history.push(
                                `/devices?deviceKey=${encodeURIComponent(deviceKey)}`,
                            );
                    }}
                />
            )}
        </div>
    );
}

export default TipLibrary;
